import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChartCard } from '@/widgets/BarChart';
import type { ReasonAmountItem, ReasonCountItem } from '@/entities/dashboards';

type Props = {
  /** Название продукта — выводится заголовком ряда */
  productName: string;
  /** ТОП-5 причин мисселинга по сумме страховой премии (подсрез этого продукта) */
  topReasonsByAmount: ReasonAmountItem[];
  /** ТОП-5 причин мисселинга по количеству диалогов (подсрез этого продукта) */
  topReasonsByCount: ReasonCountItem[];
};

/**
 * Ряд «ТОП причин мисселинга по продукту»: заголовок-имя продукта и пара виджетов
 * (по сумме страховой премии и по количеству диалогов). Максимумы для нормировки
 * полос считаются локально на данных конкретного продукта.
 */
export const ProductReasonsRow: FC<Props> = ({ productName, topReasonsByAmount, topReasonsByCount }) => {
  const { t } = useTranslation('missellingDashboard');

  const reasonsAmountMax = useMemo(
    () => Math.max(...topReasonsByAmount.map((reasonItem) => reasonItem.total), 1),
    [topReasonsByAmount],
  );
  const reasonsCountMax = useMemo(
    () => Math.max(...topReasonsByCount.map((reasonItem) => reasonItem.count), 1),
    [topReasonsByCount],
  );

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{productName}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BarChartCard
          title={t('charts.topReasonsByAmount')}
          items={topReasonsByAmount.map((reasonItem) => ({
            label: reasonItem.name,
            value: reasonItem.total,
          }))}
          maxValue={reasonsAmountMax}
          format="currency-rub"
          labelWidth="wide"
        />
        <BarChartCard
          title={t('charts.topReasonsByCount')}
          items={topReasonsByCount.map((reasonItem) => ({
            label: reasonItem.name,
            value: reasonItem.count,
          }))}
          maxValue={reasonsCountMax}
          format="integer"
          labelWidth="wide"
        />
      </div>
    </div>
  );
};
